/**
 * The `#state=…&code=…` a Keycloak redirect leaves in the address bar.
 *
 * keycloak-js removes it itself — but only once `init()` gets that far, and `init()` first waits
 * up to 10 s for its third-party-cookie iframe on auth.lupaapp.ru. On a network that drops
 * requests to us (it happens on Beeline for minutes at a time) that wait times out, `init()`
 * rejects, and the fragment stays. The next `keycloak.login()` then used `location.href` as its
 * redirect URI, fragment included; Keycloak echoed it back in a `Location` header that, together
 * with its cookies, outgrew nginx's proxy buffer, and the sign-in page answered 502 on every
 * reload (nginx error.log, 01.10.2026 15:11, 21:13, 21:59). A spent code is useless anyway: it is
 * single-use and only valid for the login attempt that produced it.
 */

// Exactly what keycloak-js reads from a standard-flow callback (parseCallbackUrl).
const CALLBACK_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
  'error_uri',
  'kc_action_status',
  'kc_action',
]

/**
 * `url` without the Keycloak callback parameters in its fragment, or `url` unchanged when it
 * carries none. Recognised by the same rule keycloak-js uses — `state` plus `code` or `error` — so
 * an ordinary in-page anchor such as `#pricing` is never touched.
 */
export function withoutOAuthCallback(url: string): string {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url
  }
  if (parsed.hash.length <= 1) return url

  const params = new URLSearchParams(parsed.hash.slice(1))
  if (!params.has('state') || !(params.has('code') || params.has('error'))) return url

  for (const name of CALLBACK_PARAMS) params.delete(name)
  const rest = params.toString()
  parsed.hash = rest ? `#${rest}` : ''
  return parsed.toString()
}

/** Removes a leftover callback from the address bar without a navigation. */
export function dropOAuthCallbackFromLocation(): void {
  const clean = withoutOAuthCallback(window.location.href)
  if (clean !== window.location.href) {
    window.history.replaceState(window.history.state, '', clean)
  }
}

// ── "Did this page load finish a sign-in the user asked for?" ────────────────
//
// The site root shows the landing to everyone, signed in or not: someone who typed lupaapp.ru
// wants the site, and the landing's header already offers «Личный кабинет». The one exception is
// coming back from the login page — every sign-in button (the landing's, registration, password
// reset) returns to the root, and the person who just typed their password expects the cabinet.
// Keycloak's redirect carries nothing that tells a deliberate sign-in from the silent `check-sso`
// round trip on every page load, so the deliberate one leaves a mark for the trip.
// sessionStorage: it survives the redirect within the tab and nothing else.

const SIGN_IN_STARTED_KEY = 'lupa.signInStarted'

/** Called when a login redirect starts. A silent check (`prompt=none`) is not a sign-in. */
export function markSignInStarted(prompt: string | undefined): void {
  if (prompt === 'none') return
  try {
    sessionStorage.setItem(SIGN_IN_STARTED_KEY, '1')
  } catch {
    // Private mode or a full quota: the user lands on the landing and clicks «Личный кабинет».
  }
}

/** Reads and clears the mark: it describes one round trip, not the tab's whole life. */
export function takeSignInStarted(): boolean {
  try {
    const started = sessionStorage.getItem(SIGN_IN_STARTED_KEY) === '1'
    sessionStorage.removeItem(SIGN_IN_STARTED_KEY)
    return started
  } catch {
    return false
  }
}
